import { useMemo } from 'react'
import { queryOptions, useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, request } from '@/lib/api'
import { toast } from '@/components/ui/Toaster'
import { adminMeQuery } from '../admin/shared/api'
import type { CrmAccount, CrmAccountsResult, CrmCaseKind, CrmCasesResult, CrmChildList, CrmChildrenResult, CrmDonorsResult, CrmOverdueResult, CrmOverdueSort, CrmPortfolioResult, CrmRecentResult, CrmSearchResult, Donor, ErpGraph, ErpSettings, ErpSettingsPatch, CrmState, LinkDonorInput, LinkDonorResult, RevealResult } from './contract'
import type { CrmInvoicesResult } from './crm-invoices'
import { CRM_PAGE_IDS, crmDonorQuery, crmIdBatches, linkErrorMessage, mergeCrm, type CrmView } from './fichas/doador/crm'
import { createErpIndex, type ErpIndex } from './graph'

/* ERP · HTTP e consultas do cliente. Um grafo só para todas as fichas e telas (GET /admin/erp/graph), indexado uma vez
   por createErpIndex; as telas usam useErp(). */

export const erpKeys = {
    graph: ['panel', 'admin', 'erp', 'graph'] as const,
    settings: ['panel', 'admin', 'erp', 'settings'] as const,
    crm: ['panel', 'admin', 'erp', 'crm'] as const,
}

/** Dados do CRM fica alguns minutos no cliente (o servidor também guarda; cada chamada ao Hub conta). */
const CRM_STALE_MS = 5 * 60_000
/** Os "chegaram por último" mudam mais: 2 minutos (o servidor guarda o mesmo tempo). */
const CRM_RECENT_STALE_MS = 2 * 60_000

/** Nome e contato mascarado (CRM, pelo Hub) só destes doadores; em lotes de uma página. Sem crmId, nada é pedido. */
export function useCrmDonors(donors: readonly Pick<Donor, 'id' | 'crmId'>[]): CrmView {
    const batches = crmIdBatches(donors)
    const results = useQueries({
        queries: batches.map(ids => ({
            queryKey: [...erpKeys.crm, 'donors', ...ids],
            queryFn: () => request<CrmDonorsResult>('POST', '/admin/erp/donors/crm', { ids }),
            staleTime: CRM_STALE_MS,
            retry: false,
        })),
    })
    return mergeCrm(results.map(r => r.data ?? (r.isError ? { state: 'unavailable' as const, items: {} } : undefined)))
}

/** Nome e contato mascarado pelo GUID do CRM (linhas da inadimplência), só dos visíveis; chave = GUID minúsculo. */
export function useCrmDonorsByCrmId(crmIds: readonly string[]): CrmView {
    const ids = [...new Set(crmIds.map(id => id.toLowerCase()))]
    const batches: string[][] = []
    for (let i = 0; i < ids.length; i += CRM_PAGE_IDS) batches.push(ids.slice(i, i + CRM_PAGE_IDS))
    const results = useQueries({
        queries: batches.map(part => ({
            queryKey: [...erpKeys.crm, 'by-crm-id', ...part],
            queryFn: () => request<CrmDonorsResult>('POST', '/admin/erp/donors/crm', { crmIds: part }),
            staleTime: CRM_STALE_MS,
            retry: false,
        })),
    })
    return mergeCrm(results.map(r => r.data ?? (r.isError ? { state: 'unavailable' as const, items: {} } : undefined)))
}

/** Contas (empresas) do CRM pedidas de uma vez ao servidor (o Hub aceita até 50 por chamada). */
export const CRM_ACCOUNT_BATCH = 50
/** O servidor guarda 2 minutos; o cliente, o mesmo. */
const CRM_ACCOUNT_STALE_MS = 2 * 60_000
export type CrmAccountsView = { state: CrmState | 'loading'; items: Record<string, CrmAccount> }
const combineAccounts = (results: { data?: CrmAccountsResult; isError: boolean }[]): CrmAccountsView =>
    mergeCrm(results.map(r => r.data ?? (r.isError ? { state: 'unavailable' as const, items: {} } : undefined)))

/** Nome das contas (empresas) do CRM pelo GUID, só das que a tela mostra; chave = GUID minúsculo. Sem ids, nada é
    pedido (state 'off'). Ids ordenados: a mesma lista em outra ordem usa o mesmo cache. */
export function useCrmAccounts(crmIds: readonly string[]): CrmAccountsView {
    const ids = [...new Set(crmIds.map(id => id.toLowerCase()))].sort()
    const batches: string[][] = []
    for (let i = 0; i < ids.length; i += CRM_ACCOUNT_BATCH) batches.push(ids.slice(i, i + CRM_ACCOUNT_BATCH))
    return useQueries({
        queries: batches.map(part => ({
            queryKey: [...erpKeys.crm, 'accounts', ...part],
            queryFn: () => request<CrmAccountsResult>('POST', '/admin/erp/accounts/crm', { crmIds: part }),
            staleTime: CRM_ACCOUNT_STALE_MS,
            retry: false,
        })),
        combine: combineAccounts,
    })
}

/** Busca por nome (3+ letras) ou CPF no CRM (vai no corpo). `enabled` false no exemplo. */
export function useCrmSearch(raw: string, enabled: boolean) {
    // nome ou CPF válido; vai só no corpo do POST (nunca na URL nem no log)
    const q = crmDonorQuery(raw)?.q ?? null
    return useQuery({
        queryKey: [...erpKeys.crm, 'search', q],
        queryFn: () => request<CrmSearchResult>('POST', '/admin/erp/donors/search', { q }),
        enabled: enabled && q !== null,
        staleTime: CRM_STALE_MS,
        retry: false,
    })
}

/** Os doadores que chegaram por último no CRM (abertura de "Todos os doadores"). `enabled` false no exemplo. */
export function useCrmRecent(enabled: boolean) {
    return useQuery({
        queryKey: [...erpKeys.crm, 'recent'],
        queryFn: () => request<CrmRecentResult>('GET', '/admin/erp/donors/recent'),
        enabled,
        staleTime: CRM_RECENT_STALE_MS,
        retry: false,
    })
}

/** Enquanto o servidor faz a 1ª leitura do CRM, pergunta de novo a cada 15 s; depois, a cada 5 min (o servidor relê a cada 30). */
const crmFactsRefetch = (state: string | undefined) => (state === 'loading' ? 15_000 : 5 * 60_000)

/** Carteira do CRM: ativos, retenção (2/7/12/24 meses), entradas × saídas, cancelamentos e canais. Só números. */
export function useCrmPortfolio(enabled = true) {
    return useQuery({
        queryKey: [...erpKeys.crm, 'portfolio'],
        queryFn: () => request<CrmPortfolioResult>('GET', '/admin/erp/crm/portfolio'),
        enabled,
        staleTime: 60_000,
        refetchInterval: q => crmFactsRefetch(q.state.data?.state),
        retry: false,
    })
}

export type CrmOverdueParams = { page?: number; limit?: number; sort?: CrmOverdueSort; minMonths?: number }

/** Inadimplência por doador (GUID do CRM + números) e o resumo. O nome: useCrmDonors só das linhas visíveis. */
export function useCrmOverdue(params: CrmOverdueParams = {}, enabled = true) {
    const qs = new URLSearchParams()
    for (const [k, v] of Object.entries(params)) if (v !== undefined) qs.set(k, String(v))
    const suffix = qs.size ? `?${qs}` : ''
    return useQuery({
        queryKey: [...erpKeys.crm, 'overdue', params],
        queryFn: () => request<CrmOverdueResult>('GET', `/admin/erp/crm/overdue${suffix}`),
        enabled,
        staleTime: 60_000,
        refetchInterval: q => crmFactsRefetch(q.state.data?.state),
        retry: false,
    })
}

export type CrmInvoicesParams = { page?: number; limit?: number }

/** Faturas do mês no CRM: totais, por forma de pagamento, lotes do cartão e a página das em atraso (GUID + números).
    O nome: useCrmDonorsByCrmId só das linhas visíveis. */
export function useCrmInvoices(params: CrmInvoicesParams = {}, enabled = true) {
    return useQuery({
        queryKey: [...erpKeys.crm, 'invoices', params],
        queryFn: () => request<CrmInvoicesResult>('GET', `/admin/erp/crm/invoices${crmQs(params)}`),
        enabled,
        staleTime: 60_000,
        refetchInterval: q => crmFactsRefetch(q.state.data?.state),
        retry: false,
    })
}

const crmQs = (params: Record<string, string | number | boolean | undefined>) => {
    const qs = new URLSearchParams()
    for (const [k, v] of Object.entries(params)) if (v !== undefined) qs.set(k, String(v))
    return qs.size ? `?${qs}` : ''
}

export type CrmCasesParams = { status?: 'open' | 'closed' | 'all'; kind?: CrmCaseKind; subject?: string; overdue?: boolean; osp?: string; page?: number; limit?: number }

/** Ocorrências do CRM (sem texto livre): resumo (por assunto, pedido especial × prazo, cancelamentos e retenção, por OSP)
    e a lista filtrada. O nome do doador: useCrmDonorsByCrmId só das linhas visíveis. */
export function useCrmCases(params: CrmCasesParams = {}, enabled = true) {
    return useQuery({
        queryKey: [...erpKeys.crm, 'cases', params],
        queryFn: () => request<CrmCasesResult>('GET', `/admin/erp/crm/cases${crmQs(params)}`),
        enabled,
        staleTime: 60_000,
        refetchInterval: q => crmFactsRefetch(q.state.data?.state),
        retry: false,
    })
}

export type CrmChildrenParams = { list?: CrmChildList; osp?: string; page?: number; limit?: number }

/** Crianças do CRM por OSP (apadrinhadas, disponíveis, reservadas, desativadas) e a lista pedida (perto do prazo,
    no prazo, sem padrinho, desativadas há pouco). Criança só por código e primeiro nome. */
export function useCrmChildren(params: CrmChildrenParams = {}, enabled = true) {
    return useQuery({
        queryKey: [...erpKeys.crm, 'children', params],
        queryFn: () => request<CrmChildrenResult>('GET', `/admin/erp/crm/children${crmQs(params)}`),
        enabled,
        staleTime: 60_000,
        refetchInterval: q => crmFactsRefetch(q.state.data?.state),
        retry: false,
    })
}

/** Liga um doador (da base ou só do CRM) a igreja, embaixador, empresa ou evento: o servidor faz o ensure antes. */
export function useLinkDonor() {
    const client = useQueryClient()
    return useMutation({
        mutationFn: (body: LinkDonorInput) => request<LinkDonorResult>('POST', '/admin/erp/donors/link', body),
        onSuccess: r => {
            void client.invalidateQueries({ queryKey: erpKeys.graph })
            // quem acabou de ser ligado passa a abrir a ficha nos "chegaram por último"
            void client.invalidateQueries({ queryKey: [...erpKeys.crm, 'recent'] })
            toast({ message: r.message })
        },
        onError: e => toast({ message: linkErrorMessage(e instanceof ApiError ? e : null), tone: 'err' }),
    })
}

export const erpGraphQuery = queryOptions({
    queryKey: erpKeys.graph,
    queryFn: () => request<ErpGraph>('GET', '/admin/erp/graph'),
    staleTime: 60_000,
    refetchInterval: 120_000,
})

export const erpSettingsQuery = queryOptions({
    queryKey: erpKeys.settings,
    queryFn: () => request<ErpSettings>('GET', '/admin/erp/settings'),
    staleTime: 60_000,
})

/** Grafo + índices. `q` é null enquanto carrega. */
export function useErp(): { query: ReturnType<typeof useQuery<ErpGraph>>; q: ErpIndex | null } {
    const query = useQuery(erpGraphQuery)
    const q = useMemo(() => (query.data ? createErpIndex(query.data) : null), [query.data])
    return { query, q }
}

/** Mostrar CPF/e-mail/celular completos de um doador (fica na auditoria). */
export function useReveal() {
    return useMutation({
        mutationFn: (body: { id: string; reason: string }) => request<RevealResult>('POST', '/admin/erp/reveal', body),
        onSuccess: r => toast({ message: r.message }),
        onError: () => toast({ message: 'Não deu para mostrar os dados. Tente de novo.', tone: 'err' }),
    })
}

export function useSaveErpSettings() {
    const client = useQueryClient()
    return useMutation({
        mutationFn: (patch: ErpSettingsPatch) => request<ErpSettings>('PUT', '/admin/erp/settings', patch),
        onSuccess: s => {
            client.setQueryData(erpKeys.settings, s)
            toast({ message: 'Salvo.' })
        },
        onError: () => toast({ message: 'Não deu para salvar. Tente de novo.', tone: 'err' }),
    })
}

/** Perfil da pessoa (para frentes e permissões). */
export const useAdminMe = () => useQuery(adminMeQuery).data ?? null
